/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  OnInit,
  Input,
  HostBinding,
  inject,
  OnChanges,
  ChangeDetectionStrategy,
  Injector,
  booleanAttribute,
  SimpleChanges,
  ContentChild,
  TemplateRef,
} from '@angular/core';
import clsx from 'clsx';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  WithDefaultValidatorProps,
  composeValidatorFn,
  hasChangedAnyDefaultValidatorProps,
} from '../utils/defaultValidator';
import { UiFormReactiveConfigService } from '../form-reactive-config.service';
import { RadioIsDisabledPipe } from './radio-is-disabled.pipe';
import { IconCloseComponent, IconInfoComponent } from '@pedix-workspace/angular-ui-icons';

let INPUT_UID = 0;

export type InputRadioGroupLayout = 'stacked' | 'inline';
export type InputRadioGroupLabelSize = 'xs' | 'sm' | 'md';
export type InputRadioGroupValueType = any | undefined | null;

@Component({
    selector: 'pxw-input-radio-group',
    templateUrl: './input-radio-group.component.html',
    styleUrls: ['./input-radio-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: InputRadioGroupComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: InputRadioGroupComponent,
        },
    ],
    imports: [
        NgClass,
        FormsModule,
        NgTemplateOutlet,
        RadioIsDisabledPipe,
        IconInfoComponent,
        IconCloseComponent,
    ]
})
export class InputRadioGroupComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator, WithDefaultValidatorProps
{
  @ContentChild('radioItemTemplate') radioItemTemplate: TemplateRef<any> | undefined;
  @ContentChild('itemTemplate') itemTemplate: TemplateRef<any> | undefined;

  @Input({ required: true }) name: string;
  @Input() label: string;
  @Input() labelSize: InputRadioGroupLabelSize = 'sm';
  @Input() helper: string;
  @Input() legend: string;
  @Input() autofocus: boolean;
  @Input() messages: { [key: string]: string } = {};
  @Input() displayErrors = false;
  @Input() labelKey: string;
  @Input() valueKey: string;
  @Input() options: any[] = [];
  @Input() layout: InputRadioGroupLayout = 'stacked';
  @Input() disabledValues: any[];
  @Input() collapsible = false;

  @Input({ transform: booleanAttribute }) required?: boolean;

  @HostBinding('class') get classNames() {
    return clsx('radio-group', 'ui-input', this.layout, {
      collapsed: this.collapsible && this.hasValue,
    });
  }

  private injector = inject(Injector);

  protected inputId: string;
  protected value: InputRadioGroupValueType;
  protected isDisabled: boolean;

  private onChange: (value: InputRadioGroupValueType) => void;
  private onTouched: () => void;
  private onValidatorChange: () => void;
  private validatorFn: ValidatorFn | null;
  private formReactiveConfigService = inject(UiFormReactiveConfigService);

  get formControl() {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl) {
      return ngControl.control as FormControl;
    }
    return null;
  }

  get isRequired() {
    return this.formControl?.hasValidator(Validators.required) || this.required === true;
  }

  get isValid() {
    return this.formControl?.valid;
  }

  get isTouched() {
    return this.formControl?.touched;
  }

  get errorEntries(): [string, any][] {
    return Object.entries(this.formControl?.errors || {});
  }

  get shouldDisplayErrors() {
    return (this.displayErrors || this.isTouched) && !this.isValid && !this.isDisabled;
  }

  get isAllSelected() {
    return this.value?.length === this.options.length;
  }

  get hasValue() {
    return this.value != null;
  }

  get selectedOption() {
    if (!this.hasValue) {
      return null;
    }
    if (this.valueKey) {
      return this.options.find(option => option[this.valueKey] === this.value);
    }
    return this.options.find(option => option === this.value);
  }

  ngOnInit(): void {
    this.inputId = `input-radio-group-${++INPUT_UID}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasChangedAnyDefaultValidatorProps(changes)) {
      this.validatorFn = composeValidatorFn(this);

      if (this.formControl) {
        this.formControl.updateValueAndValidity();
      }
    }
  }

  writeValue(value: InputRadioGroupValueType) {
    this.value = value;
  }
  registerOnChange(onChange: (value: InputRadioGroupValueType) => void) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorFn?.(control) || null;
  }
  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  onOptionSelected(selectedOption: any) {
    const selectedValue = this.valueKey ? selectedOption[this.valueKey] : selectedOption;

    this.writeValue(selectedValue);
    this.onChange(selectedValue);
    this.onValidatorChange();
    this.onTouched();
  }

  onUnselect() {
    this.writeValue(null);
    this.onChange(null);
    this.onValidatorChange();
    this.onTouched();
  }

  protected getErrorMessage([key, data]: [string, any]) {
    return this.messages?.[key] || this.formReactiveConfigService.getErrorMessage(key, data);
  }
}
